import React, { useEffect, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { X, Loader2, AlertCircle, CheckCircle, Clock } from 'lucide-react';
import { Button } from '../UI/Button';
import paymentService from '../../services/paymentService';

interface Props {
  isOpen: boolean;
  contractId: string;
  contractTitle: string;
  amount: number;
  currency: string;
  onClose: () => void;
  onPaid: () => void;
}

const formatCountdown = (ms: number) => {
  if (ms <= 0) return '已过期';
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

export const PaymentModal: React.FC<Props> = ({
  isOpen,
  contractId,
  contractTitle,
  amount,
  currency,
  onClose,
  onPaid,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<number>(0);
  const [tradeNo, setTradeNo] = useState<string | null>(null);
  const [now, setNow] = useState<number>(Date.now());
  const [polling, setPolling] = useState(false);
  const [paid, setPaid] = useState(false);
  const [simulating, setSimulating] = useState(false);

  // Create payment order on open
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await paymentService.createPaymentOrder(contractId);
        if (cancelled) return;
        if (res.success) {
          const data = (res.data as any)?.data || res.data;
          setQrCodeUrl(data.qrCodeUrl);
          setExpiresAt(Number(data.expiresAt));
          setTradeNo(data.tradeNo);
        } else {
          setError((res as any).error || '创建支付订单失败');
        }
      } catch (err: any) {
        if (!cancelled) setError(err?.response?.data?.error || '创建支付订单失败');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, contractId]);

  // Tick countdown
  useEffect(() => {
    if (!isOpen) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [isOpen]);

  // Poll payment status while modal open
  useEffect(() => {
    if (!isOpen || !tradeNo || paid) return;
    setPolling(true);
    const id = setInterval(async () => {
      try {
        const res = await paymentService.getPaymentInfo(contractId);
        if (res.success) {
          const data = (res.data as any)?.data || res.data;
          if (data?.paymentStatus === 'FULLY_FUNDED') {
            setPaid(true);
            setTimeout(() => { onPaid(); onClose(); }, 1500);
          }
        }
      } catch {}
    }, 4000);
    return () => { clearInterval(id); setPolling(false); };
  }, [isOpen, tradeNo, paid, contractId, onPaid, onClose]);

  const handleSimulate = async () => {
    setSimulating(true);
    try {
      await paymentService.simulatePayment(contractId);
      // poller will pick up the FULLY_FUNDED state
    } finally {
      setSimulating(false);
    }
  };

  if (!isOpen) return null;
  const remaining = expiresAt ? expiresAt - now : 0;
  const isStubbed = qrCodeUrl?.startsWith('alipay-stub://');

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <h2 className="text-lg font-semibold text-slate-900">支付合同款项</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div>
            <div className="text-xs text-slate-500 mb-1">{contractTitle}</div>
            <div className="text-3xl font-bold text-slate-900">
              {currency === 'CNY' ? '¥' : currency} {amount.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
            </div>
          ) : error ? (
            <div className="flex items-start gap-3 rounded-md bg-red-50 border border-red-200 p-3 text-sm">
              <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
              <p className="text-red-900">{error}</p>
            </div>
          ) : paid ? (
            <div className="flex flex-col items-center gap-3 py-8">
              <CheckCircle className="w-16 h-16 text-emerald-500" />
              <p className="text-base font-medium text-slate-900">支付成功</p>
              <p className="text-sm text-slate-500">款项已托管，创作者可开始工作</p>
            </div>
          ) : qrCodeUrl ? (
            <>
              <div className="flex flex-col items-center gap-3 py-3">
                <div className="rounded-lg border border-slate-200 p-4 bg-white">
                  <QRCodeCanvas value={qrCodeUrl} size={200} level="M" />
                </div>
                <p className="text-sm text-slate-600">使用支付宝 App 扫码支付</p>
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <Clock className="w-3.5 h-3.5" />
                  <span>剩余支付时间：{formatCountdown(remaining)}</span>
                </div>
              </div>

              {isStubbed && (
                <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs space-y-2">
                  <p className="text-amber-900 font-medium">⚙️ 开发模式</p>
                  <p className="text-amber-800">支付宝直付通尚未接入，扫码不会跳转到真实支付。点击下方按钮模拟支付完成。</p>
                  <Button variant="outline" onClick={handleSimulate} disabled={simulating}>
                    {simulating ? '模拟中...' : '模拟支付完成'}
                  </Button>
                </div>
              )}

              {polling && !paid && (
                <p className="text-xs text-center text-slate-400">正在等待支付确认...</p>
              )}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;
